<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRight, Cpu, KeyRound, Lock, MessageSquareText, ShieldCheck, X } from 'lucide-vue-next'
import UpdateAnnouncementDialog from './UpdateAnnouncementDialog.vue'
import { securityAnnouncement, securityAnnouncementActive, securityHistory, SECURITY_STORAGE_KEY, withSecurityAnnouncementLock } from '@/services/security-announcement'
import { securityCopy } from '@/i18n/security'

const props = defineProps<{ ready: boolean; blocked: boolean }>()
const { locale } = useI18n()
const copy = computed(() => securityCopy[locale.value === 'en' ? 'en' : 'zh-CN'])
const route = useRoute(), router = useRouter()
const onReport = computed(() => route.name === 'security')
const seen = ref(securityHistory.hasSeen())
const active = ref(securityAnnouncementActive())
const pageVisible = ref(document.visibilityState === 'visible')
const visible = ref(false), closing = ref(false)
const pending = computed(() => active.value && !seen.value)
const eligible = computed(() => props.ready && !props.blocked && !onReport.value && pageVisible.value && !seen.value && (active.value || visible.value))
let disposed = false, requesting = false, goToReport = false
let controller: AbortController | undefined
let finish: (() => void) | undefined
let expiryTimer: ReturnType<typeof setTimeout> | undefined
let previousFocus: HTMLElement | null = null

function refresh(): void {
  active.value = securityAnnouncementActive()
  seen.value = securityHistory.hasSeen()
  pageVisible.value = document.visibilityState === 'visible'
}
function scheduleExpiry(): void {
  clearTimeout(expiryTimer)
  const remaining = Date.parse(securityAnnouncement.expiresAt) - Date.now()
  if (remaining > 0) expiryTimer = setTimeout(() => { refresh(); scheduleExpiry() }, Math.min(remaining, 2_147_483_647))
}
async function check(): Promise<void> {
  await nextTick()
  if (disposed || requesting || visible.value || closing.value || !eligible.value) return
  requesting = true
  controller = new AbortController()
  try {
    await withSecurityAnnouncementLock(async () => {
      refresh()
      if (disposed || !eligible.value || !active.value) return
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
      await new Promise<void>(resolve => { finish = resolve; visible.value = true })
    }, controller.signal)
  } finally {
    requesting = false
    controller = undefined
    if (!disposed && eligible.value && !closing.value && !visible.value) void check()
  }
}
function dismiss(report = false): void {
  if (!visible.value) return
  closing.value = true
  goToReport = report
  securityHistory.acknowledge()
  seen.value = true
  visible.value = false
}
async function closed(): Promise<void> {
  if (goToReport) {
    goToReport = false
    await router.push(securityAnnouncement.reportPath)
  }
  closing.value = false
  finish?.(); finish = undefined
  if (!props.blocked && pageVisible.value) {
    const target = !onReport.value && previousFocus?.isConnected && previousFocus !== document.body
      ? previousFocus : document.querySelector<HTMLElement>('#main-content')
    target?.focus({ preventScroll: true })
  }
  refresh()
  void check()
}
function storageChanged(event: StorageEvent): void {
  if (event.key === SECURITY_STORAGE_KEY || event.key === null) refresh()
}
watch(eligible, allowed => {
  if (allowed) void check()
  else {
    controller?.abort()
    if (visible.value) { closing.value = true; visible.value = false }
  }
}, { immediate: true, flush: 'post' })
onMounted(() => {
  document.addEventListener('visibilitychange', refresh)
  window.addEventListener('pageshow', refresh)
  window.addEventListener('storage', storageChanged)
  scheduleExpiry()
})
onBeforeUnmount(() => {
  disposed = true
  clearTimeout(expiryTimer)
  controller?.abort()
  finish?.()
  document.removeEventListener('visibilitychange', refresh)
  window.removeEventListener('pageshow', refresh)
  window.removeEventListener('storage', storageChanged)
})
</script>

<template>
  <el-dialog :model-value="visible" class="security-notice" width="41rem" align-center append-to-body destroy-on-close
    :show-close="false" :close-on-click-modal="false" :close-on-press-escape="true"
    @update:model-value="(value: boolean) => { if (!value) dismiss() }" @closed="closed">
    <template #header="{ titleId }">
      <div class="security-notice-top">
        <div class="security-eyebrow">
          <span class="security-eyebrow-dot" aria-hidden="true" />
          <ShieldCheck class="security-eyebrow-icon" aria-hidden="true" />
          <span>{{ copy.notice.kicker }}</span>
        </div>
        <button class="security-close" type="button" :aria-label="copy.close" @click="dismiss()">
          <X aria-hidden="true" />
        </button>
      </div>
      <div class="security-notice-headline">
        <h2 :id="titleId">{{ copy.notice.title }}</h2>
        <p class="security-notice-intro-text">{{ copy.notice.intro }}</p>
      </div>
    </template>

    <div class="security-notice-body-inner">
      <!-- 核心安全承诺卡片组 -->
      <div class="security-assurance-grid">
        <div v-for="(fact, index) in copy.facts" :key="fact.title" class="security-assurance-card">
          <div class="security-assurance-icon-box" :class="`is-${index}`">
            <KeyRound v-if="index === 0" aria-hidden="true" />
            <Cpu v-else-if="index === 1" aria-hidden="true" />
            <ShieldCheck v-else aria-hidden="true" />
          </div>
          <div class="security-assurance-content">
            <div class="security-assurance-header">
              <strong class="security-assurance-title">{{ fact.title }}</strong>
              <span class="security-assurance-tag">{{ fact.label }}</span>
            </div>
            <p class="security-assurance-detail">{{ fact.detail }}</p>
          </div>
        </div>
      </div>

      <aside class="security-notice-official" :aria-label="copy.notice.officialResponse">
        <span class="security-notice-official-icon"><MessageSquareText aria-hidden="true" /></span>
        <div class="security-notice-official-content">
          <strong>{{ copy.notice.officialResponse }}</strong>
          <p>{{ copy.notice.officialContext }}</p>
        </div>
      </aside>

      <!-- 架构安全与辟谣背书条 -->
      <div class="security-trust-callout">
        <div class="security-trust-callout-icon" aria-hidden="true">
          <Lock />
        </div>
        <div class="security-trust-callout-text">
          <p>{{ copy.notice.detail }}</p>
        </div>
      </div>

      <!-- 签名与透明度指纹 -->
      <div class="security-signature">
        <div class="security-meta-author-wrap">
          <span class="security-meta-dot" aria-hidden="true" />
          <span class="security-signature-author">{{ copy.author }}</span>
          <span class="security-meta-sep">·</span>
          <time :datetime="securityAnnouncement.updatedAt">{{ securityAnnouncement.updatedAt.replaceAll('-', '.') }}</time>
        </div>
        <span class="security-meta-guarantee">代码指纹 SHA-256 开放核查</span>
      </div>
    </div>

    <template #footer>
      <div class="security-notice-actions">
        <el-button class="security-acknowledge" @click="dismiss()">{{ copy.acknowledge }}</el-button>
        <el-button type="primary" class="security-read-report" @click="dismiss(true)">
          <span>{{ copy.readReport }}</span>
          <ArrowRight aria-hidden="true" />
        </el-button>
      </div>
    </template>
  </el-dialog>
  <UpdateAnnouncementDialog :ready="ready" :blocked="blocked || onReport || pending || visible || closing" />
</template>

<style>
.el-dialog.security-notice {
  --security-accent: #0284c7;
  --security-accent-hover: #0369a1;
  --security-accent-soft: rgba(2, 132, 199, 0.08);
  --security-accent-glow: rgba(2, 132, 199, 0.18);
  --security-accent-border: rgba(2, 132, 199, 0.22);
  display: flex;
  flex-direction: column;
  width: min(42rem, calc(100vw - 1.5rem));
  max-height: calc(100dvh - 3.5rem);
  padding: 0;
  overflow: hidden;
  border: 0.0625rem solid color-mix(in srgb, var(--security-accent) 24%, var(--border));
  border-radius: var(--radius-card, 1.125rem);
  background: var(--surface);
  box-shadow: 0 1.5rem 3.5rem -0.75rem rgba(0, 0, 0, 0.32), 0 0 0 0.0625rem rgba(255, 255, 255, 0.04);
}

:root[data-theme='dark'] .el-dialog.security-notice {
  --security-accent: #38bdf8;
  --security-accent-hover: #7dd3fc;
  --security-accent-soft: rgba(56, 189, 248, 0.1);
  --security-accent-glow: rgba(56, 189, 248, 0.2);
  --security-accent-border: rgba(56, 189, 248, 0.3);
}

.security-notice .el-dialog__header {
  flex: none;
  padding: 1.25rem 1.75rem 0.75rem;
  margin: 0;
  background: radial-gradient(circle at 92% 12%, var(--security-accent-soft), transparent 55%),
              var(--surface-hover);
  border-bottom: 0.0625rem solid var(--border);
}

.security-notice-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.625rem;
}

.security-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.21875rem 0.625rem;
  border-radius: 999rem;
  background: color-mix(in srgb, var(--green, #10b981) 12%, var(--surface));
  border: 0.0625rem solid color-mix(in srgb, var(--green, #10b981) 25%, transparent);
  color: var(--green, #10b981);
  font-size: 0.71875rem;
  font-weight: 650;
  letter-spacing: 0.03em;
}

.security-eyebrow-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: var(--green, #10b981);
  box-shadow: 0 0 0.375rem color-mix(in srgb, var(--green, #10b981) 60%, transparent);
  flex: none;
}

.security-eyebrow-icon {
  width: 0.8125rem;
  height: 0.8125rem;
  flex: none;
}

.security-close {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  flex: none;
  color: var(--muted);
  border: 0.0625rem solid transparent;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.security-close:hover {
  background: var(--surface-subtle);
  border-color: var(--border);
  color: var(--text-strong);
  transform: rotate(90deg);
}

.security-close svg {
  width: 0.9375rem;
  height: 0.9375rem;
}

.security-notice-headline {
  display: flex;
  flex-direction: column;
  gap: 0.3125rem;
}

.security-notice h2 {
  margin: 0;
  color: var(--text-strong);
  font-size: 1.25rem;
  font-weight: 750;
  line-height: 1.35;
  letter-spacing: -0.015em;
}

.security-notice-intro-text {
  margin: 0;
  color: var(--muted-strong);
  font-size: 0.8125rem;
  line-height: 1.6;
}

.security-notice-official {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border: 0.0625rem solid var(--security-accent-border);
  border-radius: 0.625rem;
  background: var(--security-accent-soft);
  color: var(--text-strong);
  line-height: 1.6;
}

.security-notice-official-icon {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.4375rem;
  color: var(--security-accent-hover);
  background: var(--surface);
  border: 0.0625rem solid var(--security-accent-border);
}

.security-notice-official-icon svg { width: 1.125rem; height: 1.125rem; }
.security-notice-official-content { min-width: 0; }
.security-notice-official-content > strong { display: block; font-size: 1rem; font-weight: 700; text-wrap: balance; }
.security-notice-official-content p { margin: 0.3125rem 0 0; color: var(--muted-strong); font-size: 0.8125rem; line-height: 1.6; }

.security-notice .el-dialog__body {
  min-height: 0;
  padding: 0.875rem 1.75rem 1rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

.security-notice-body-inner {
  display: flex;
  flex-direction: column;
  gap: 0.8125rem;
}

/* 核心保障卡片组 */
.security-assurance-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.security-assurance-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  background: var(--surface-subtle);
  border: 0.0625rem solid var(--border);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1),
              border-color 0.2s ease,
              box-shadow 0.2s ease !important;
}

.security-assurance-card:hover {
  transform: translateY(-0.0625rem);
  border-color: color-mix(in srgb, var(--security-accent) 32%, var(--border));
  box-shadow: 0 0.25rem 0.875rem -0.125rem color-mix(in srgb, var(--security-accent) 10%, transparent);
}

.security-assurance-icon-box {
  display: grid;
  place-items: center;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.4375rem;
  flex: none;
  background: var(--surface);
  border: 0.0625rem solid var(--border);
  color: var(--security-accent);
  transition: transform 0.2s ease;
}

.security-assurance-card:hover .security-assurance-icon-box {
  transform: scale(1.05);
}

.security-assurance-icon-box.is-0 {
  color: #0284c7;
  background: color-mix(in srgb, #0284c7 10%, var(--surface));
  border-color: color-mix(in srgb, #0284c7 20%, transparent);
}

.security-assurance-icon-box.is-1 {
  color: #0d9488;
  background: color-mix(in srgb, #0d9488 10%, var(--surface));
  border-color: color-mix(in srgb, #0d9488 20%, transparent);
}

.security-assurance-icon-box.is-2 {
  color: #10b981;
  background: color-mix(in srgb, #10b981 10%, var(--surface));
  border-color: color-mix(in srgb, #10b981 20%, transparent);
}

:root[data-theme='dark'] .security-assurance-icon-box.is-0 {
  color: #38bdf8;
  background: color-mix(in srgb, #38bdf8 14%, var(--surface));
}

:root[data-theme='dark'] .security-assurance-icon-box.is-1 {
  color: #2dd4bf;
  background: color-mix(in srgb, #2dd4bf 14%, var(--surface));
}

:root[data-theme='dark'] .security-assurance-icon-box.is-2 {
  color: #34d399;
  background: color-mix(in srgb, #34d399 14%, var(--surface));
}

.security-assurance-icon-box svg {
  width: 1rem;
  height: 1rem;
}

.security-assurance-content {
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
  min-width: 0;
  flex: 1;
}

.security-assurance-header {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  flex-wrap: wrap;
}

.security-assurance-tag {
  display: inline-block;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.65625rem;
  font-weight: 650;
  background: var(--surface);
  border: 0.0625rem solid var(--border);
  color: var(--muted-strong);
}

.security-assurance-title {
  color: var(--text-strong);
  font-size: 0.84375rem;
  font-weight: 650;
  line-height: 1.35;
}

.security-assurance-detail {
  margin: 0;
  color: var(--muted-strong);
  font-size: 0.78125rem;
  line-height: 1.5;
}

/* 架构安全背书条 */
.security-trust-callout {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--security-accent) 6%, var(--surface-subtle));
  border: 0.0625rem solid color-mix(in srgb, var(--security-accent) 18%, var(--border));
}

.security-trust-callout-icon {
  display: grid;
  place-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.3125rem;
  background: var(--surface);
  color: var(--security-accent);
  flex: none;
  border: 0.0625rem solid color-mix(in srgb, var(--security-accent) 22%, transparent);
}

.security-trust-callout-icon svg {
  width: 0.8125rem;
  height: 0.8125rem;
}

.security-trust-callout-text p {
  margin: 0;
  color: var(--text);
  font-size: 0.78125rem;
  line-height: 1.55;
  opacity: 0.92;
}

/* 署名栏 */
.security-signature {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
  border-top: 0.0625rem solid var(--border);
  font-size: 0.75rem;
  color: var(--muted);
}

.security-meta-author-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.security-meta-dot {
  width: 0.3125rem;
  height: 0.3125rem;
  border-radius: 50%;
  background: var(--muted);
}

.security-signature-author {
  color: var(--text-strong);
  font-weight: 600;
}

.security-meta-sep {
  color: var(--border-strong);
}

.security-signature time {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, Consolas, monospace;
}

.security-meta-guarantee {
  font-size: 0.6875rem;
  color: var(--muted);
  font-family: ui-monospace, Consolas, monospace;
}

/* 底部操作区 */
.security-notice .el-dialog__footer {
  flex: none;
  padding: 0.75rem 1.75rem 0.875rem;
  border-top: 0.0625rem solid var(--border);
  background: var(--surface);
}

.security-notice-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
}

.security-notice-actions .el-button {
  margin: 0;
  min-height: 2.375rem;
  padding: 0.4375rem 1.1875rem;
  font-size: 0.84375rem;
  font-weight: 600;
  border-radius: var(--radius-control, 0.5rem);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.security-notice-actions .security-acknowledge {
  color: var(--muted-strong);
  background: var(--surface-subtle);
  border-color: var(--border);
}

.security-notice-actions .security-acknowledge:hover {
  border-color: var(--border-strong);
  color: var(--text-strong);
  background: var(--surface-hover);
  transform: translateY(-0.0625rem);
}

.security-notice-actions .security-read-report {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%) !important;
  border-color: #0284c7 !important;
  color: #ffffff !important;
  box-shadow: 0 0.25rem 0.875rem rgba(2, 132, 199, 0.26) !important;
}

.security-notice-actions .security-read-report:hover {
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%) !important;
  border-color: #0369a1 !important;
  transform: translateY(-0.0625rem);
  box-shadow: 0 0.375rem 1.125rem rgba(2, 132, 199, 0.36) !important;
}

:root[data-theme='dark'] .security-notice-actions .security-read-report {
  background: linear-gradient(135deg, #0284c7 0%, #075985 100%) !important;
  border-color: #0284c7 !important;
  box-shadow: 0 0.25rem 0.875rem rgba(2, 132, 199, 0.32) !important;
}

:root[data-theme='dark'] .security-notice-actions .security-read-report:hover {
  background: linear-gradient(135deg, #0369a1 0%, #0c4a6e 100%) !important;
  box-shadow: 0 0.375rem 1.125rem rgba(56, 189, 248, 0.25) !important;
}

.security-read-report svg {
  width: 0.9375rem;
  height: 0.9375rem;
  margin-left: 0.125rem;
  transition: transform 0.2s ease;
}

.security-notice-actions .security-read-report:hover svg {
  transform: translateX(0.125rem);
}

.security-notice button:focus-visible {
  outline: 0.125rem solid var(--security-accent) !important;
  outline-offset: 0.125rem;
}

@media(max-width: 36rem) {
  .security-notice .el-dialog__header {
    padding: 1.25rem 1.25rem 0.875rem;
  }
  .security-notice h2 {
    font-size: 1.125rem;
  }
  .security-notice .el-dialog__body {
    padding: 1rem 1.25rem;
  }
  .security-assurance-card {
    padding: 0.75rem 0.875rem;
  }
  .security-signature {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  .security-notice .el-dialog__footer {
    padding: 0.875rem 1.25rem max(0.875rem, env(safe-area-inset-bottom));
  }
  .security-notice-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
  .security-notice-actions .el-button {
    width: 100%;
    flex: auto;
  }
}
</style>
